import {
  ModelConceptRelationship,
  ModelConceptRelationshipLabel,
} from 'src/containers/ProjectDiagram/types/data';
import { Diagram } from 'src/containers/ProjectDiagram/types/diagram';
import { Model } from 'src/containers/ProjectDiagram/types/model';
import { generateDetachedKey } from 'src/containers/ProjectDiagram/utils/detachedKey';
import { fixUnconnectedLinkPoints } from 'src/containers/ProjectDiagram/utils/fixUnconnectedLinkPoints';
import {
  createModelConceptRelationship,
  getModelConceptRelationshipKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationship';
import {
  createModelConceptRelationshipLabel,
  getModelConceptRelationshipLabelKey,
} from 'src/containers/ProjectDiagram/utils/modelConceptRelationship/createModelConceptRelationshipLabel';
import { restoreDeletedRelationship } from 'src/containers/ProjectDiagram/utils/restoreDeletedData';

type UnconnectedRelationshipOptions =
  | {
      value: string;
      fromIri?: never;
      toIri?: never;
    }
  | {
      value: string;
      fromIri: string;
      toIri?: never;
    }
  | {
      value: string;
      fromIri?: never;
      toIri: string;
    };

export const addUnconnectedModelConceptRelationship =
  ({ value, fromIri, toIri }: UnconnectedRelationshipOptions) =>
  (model: Model, diagram?: Diagram) => {
    const fromKey = generateDetachedKey();
    const toKey = generateDetachedKey();

    let link: ModelConceptRelationship;
    let linkLabel: ModelConceptRelationshipLabel;

    if (fromIri) {
      const linkKeyOverride = getModelConceptRelationshipKey({
        fromIri,
        toIri: toKey,
        value,
      });
      const linkLabelKeyOverride = getModelConceptRelationshipLabelKey({
        fromIri,
        toIri: toKey,
        value,
      });

      link = createModelConceptRelationship({
        value,
        fromIri,
        keyOverride: linkKeyOverride,
        labelKeyOverride: linkLabelKeyOverride,
      });
      linkLabel = createModelConceptRelationshipLabel({
        value,
        fromIri,
        keyOverride: linkLabelKeyOverride,
      });
    } else if (toIri) {
      const linkKeyOverride = getModelConceptRelationshipKey({
        fromIri: fromKey,
        toIri,
        value,
      });
      const linkLabelKeyOverride = getModelConceptRelationshipLabelKey({
        fromIri: fromKey,
        toIri,
        value,
      });

      link = createModelConceptRelationship({
        value,
        toIri,
        keyOverride: linkKeyOverride,
        labelKeyOverride: linkLabelKeyOverride,
      });
      linkLabel = createModelConceptRelationshipLabel({
        value,
        toIri,
        keyOverride: linkLabelKeyOverride,
      });
    } else {
      const linkKeyOverride = getModelConceptRelationshipKey({
        fromIri: fromKey,
        toIri: toKey,
        value,
      });
      const linkLabelKeyOverride = getModelConceptRelationshipLabelKey({
        fromIri: fromKey,
        toIri: toKey,
        value,
      });

      link = createModelConceptRelationship({
        value,
        keyOverride: linkKeyOverride,
        labelKeyOverride: linkLabelKeyOverride,
      });
      linkLabel = createModelConceptRelationshipLabel({
        value,
        keyOverride: linkLabelKeyOverride,
      });
    }

    model.addLinkData(link);
    model.addNodeData(linkLabel);

    restoreDeletedRelationship(link, linkLabel)(model);

    fixUnconnectedLinkPoints(link)(model, diagram);
  };
